/* eslint-disable react-compiler/react-compiler */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
/* eslint-disable consistent-return */
import React, { useMemo } from "react";

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
  fromPromise,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { useAuth0 } from "@auth0/auth0-react";

import { getDeviceId } from "analytics/utils";
import { useEmployeeStore } from "app/stores/useEmployeeStore";
import { useLayoutStore } from "app/stores/useLayoutStore";
import { EppoFeatureFlags } from "app/types/flags";
import { config } from "config";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";

type CustomApolloProviderProps = {
  children: React.ReactNode;
};

const GRAPHQL_URL_HUB_ONE_CORE = `${config.environment.HUB_ONE_CORE_API_URL}/graphql`;

// Auth0 SDK errors that mean the refresh token is gone/invalid and the only
// recovery is a fresh interactive login.
const AUTH_RECOVERY_ERROR_CODES = new Set([
  "login_required",
  "invalid_grant",
  "missing_refresh_token",
  "consent_required",
  "interaction_required",
]);

const MISSING_HUBONE_PERMISSION_ERROR_CODE = "MISSING_HUBONE_PERMISSION";

// Non-2xx responses arrive as a network error, with the GraphQL errors in `result`.
const hasErrorCode = (
  graphQLErrors: ReadonlyArray<{ extensions?: Record<string, unknown> }> | undefined,
  networkError: unknown,
  code: string,
): boolean => {
  const networkErrors =
    networkError && typeof networkError === "object" && "result" in networkError
      ? (networkError as { result?: { errors?: Array<{ extensions?: { code?: string } }> } }).result
          ?.errors
      : undefined;
  return [...(graphQLErrors ?? []), ...(networkErrors ?? [])].some(
    (err) => err?.extensions?.code === code,
  );
};

// The Auth0 post-login Action denies Hub Staff App accounts without the hubone-hub role. It also
// runs on refresh-token exchange, so an already logged-in session gets this when its token renews.
const HUBONE_ACCESS_DENIED_DESCRIPTION = "This account has no HubOne access.";

const isHubOneAccessDenied = (e: unknown): boolean => {
  if (!e || typeof e !== "object") return false;
  const { error, error_description: description } = e as {
    error?: string;
    error_description?: string;
  };
  return error === "access_denied" && description === HUBONE_ACCESS_DENIED_DESCRIPTION;
};

const isAuthRecoveryNeeded = (e: unknown): boolean => {
  if (!e || typeof e !== "object") return false;
  const code = (e as { error?: string }).error;
  return typeof code === "string" && AUTH_RECOVERY_ERROR_CODES.has(code);
};

const getAuthErrorCode = (e: unknown): string => {
  if (!e || typeof e !== "object") return "unknown";
  return (e as { error?: string }).error ?? "unknown";
};

// Shared across every in-flight request so parallel callers dedupe onto a
// single refresh. auth0-spa-js v2 rotates the refresh token on every network
// exchange and Auth0 invalidates the whole token family if the same token is
// redeemed twice, so concurrent refreshes MUST be collapsed.
let refreshTokenPromise: Promise<string> | null = null;

export function CustomApolloProvider({ children }: CustomApolloProviderProps) {
  const { getAccessTokenSilently, logout } = useAuth0();
  const { isFeatureEnabled: isDeviceClaimingEnabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.DEVICE_CLAIMING_PROCESS,
  );

  const client = useMemo(() => {
    const getToken = (forceRefresh = false): Promise<string> => {
      if (refreshTokenPromise) {
        return refreshTokenPromise;
      }
      refreshTokenPromise = getAccessTokenSilently(forceRefresh ? { cacheMode: "off" } : undefined)
        .then((token) => {
          if (!token) throw new Error("Received empty token from Auth0");
          return token;
        })
        .finally(() => {
          refreshTokenPromise = null;
        });
      return refreshTokenPromise;
    };

    const authLink = setContext(async (_, { headers }) => {
      const newHeaders = { ...headers };

      const employeeId = useEmployeeStore.getState().badgeNo;
      if (employeeId) newHeaders["x-employee-id"] = employeeId;

      if (isDeviceClaimingEnabled) newHeaders["x-device-id"] = getDeviceId();

      try {
        const accessToken = await getToken();
        newHeaders.authorization = `Bearer ${accessToken}`;
      } catch (e) {
        if (isHubOneAccessDenied(e)) {
          useLayoutStore.setState({ showMissingHubOnePermissionModal: true });
        } else if (isAuthRecoveryNeeded(e)) {
          // Refresh token is dead — can't recover silently. Force a fresh login.
          console.warn(`[auth] authLink: unrecoverable (${getAuthErrorCode(e)}) — logging out`);
          logout({ logoutParams: { returnTo: window.location.origin } });
        } else {
          console.warn("[auth] authLink: token fetch failed (transient)", e);
        }
        // Never send the request without an Authorization header; let the
        // caller surface the error instead of cascading into a 401 loop.
        throw e;
      }

      return { headers: newHeaders };
    });

    const httpLink = new HttpLink({ uri: GRAPHQL_URL_HUB_ONE_CORE });

    // -----------------------------
    // Error link: handle 401/403 + UNAUTHORIZED
    // -----------------------------
    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
      // --- Account without HubOne access (hub-one-core answers 403) ---
      // Must run before the 401/403 branch: refreshing the token won't help, it would retry forever.
      if (hasErrorCode(graphQLErrors, networkError, MISSING_HUBONE_PERMISSION_ERROR_CODE)) {
        useLayoutStore.setState({ showMissingHubOnePermissionModal: true });
        return;
      }

      // --- a) BFF verifyDeviceClaim unauthorized ---
      if (
        graphQLErrors &&
        Array.isArray(graphQLErrors) &&
        graphQLErrors?.some((err) => err?.extensions?.code === "UNAUTHORIZED")
      ) {
        useEmployeeStore.setState({ isSetForLogout: true });
        return;
      }

      // --- b) Token expired / network HTTP 401/403 ---
      const statusCode =
        networkError && "statusCode" in networkError
          ? (networkError as { statusCode?: number })?.statusCode
          : null;
      if (statusCode === 401 || statusCode === 403) {
        console.warn(
          `[auth] errorLink: ${statusCode} on ${operation.operationName} — attempting refresh`,
        );
        return fromPromise(
          getToken(true).catch((e) => {
            if (isHubOneAccessDenied(e)) {
              useLayoutStore.setState({ showMissingHubOnePermissionModal: true });
            } else if (isAuthRecoveryNeeded(e)) {
              console.warn(
                `[auth] errorLink: refresh failed (${getAuthErrorCode(e)}) — logging out`,
              );
              logout({ logoutParams: { returnTo: window.location.origin } });
            } else {
              console.warn("[auth] errorLink: refresh failed (transient)", e);
            }
            throw e;
          }),
        ).flatMap((newToken) => {
          operation.setContext(({ headers = {} }) => ({
            headers: { ...headers, authorization: `Bearer ${newToken}` },
          }));
          return forward(operation);
        });
      }
    });

    const opNameLink = new ApolloLink((operation, forward) => {
      if (
        typeof window !== "undefined" &&
        config.environment.ENVIRONMENT !== "production" &&
        operation.operationName
      ) {
        try {
          const ctx = operation.getContext();
          const currentUri = ctx.uri || GRAPHQL_URL_HUB_ONE_CORE;
          const url = new URL(currentUri, window.location.origin);
          url.searchParams.set("op", operation.operationName);
          operation.setContext({ uri: url.toString() });
        } catch (e) {
          console.warn("Failed to append operation name to URL", e);
        }
      }
      return forward(operation);
    });

    return new ApolloClient({
      link: from([authLink, opNameLink, errorLink, httpLink]),
      cache: new InMemoryCache(),
    });
  }, [getAccessTokenSilently, isDeviceClaimingEnabled]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
